import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  Layers, ChevronDown, ChevronRight, Pencil, Trash2, Loader2,
} from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import {
  assetsService,
  type AssetCategory,
  type AssetSubcategory,
  type AssetBrand,
  type AssetStatus,
} from '@/services/assets.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { usePermission } from '@/hooks/usePermission';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CategoryFormDialog } from './components/CategoryFormDialog';
import { SubcategoryFormDialog } from './components/SubcategoryFormDialog';
import { BrandFormDialog } from './components/BrandFormDialog';
import { StatusFormDialog } from './components/StatusFormDialog';
import { IdCategoriesTab } from './components/IdCategoriesTab';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

type CatDialog =
  | { type: 'category'; mode: 'create' | 'edit'; category?: AssetCategory }
  | { type: 'subcategory'; mode: 'create' | 'edit'; subcategory?: AssetSubcategory; preselectedCategoryId?: string }
  | null;

type BrandDialog = { mode: 'create' | 'edit'; brand?: AssetBrand } | null;
type StatusDialog = { mode: 'create' | 'edit'; status?: AssetStatus } | null;

const TABS = [
  { key: 'categories', label: 'Categories & Sub' },
  { key: 'brands', label: 'Brands' },
  { key: 'status', label: 'Status' },
  { key: 'settings', label: 'Settings' },
];

// ─── Categories Tab ───────────────────────────────────────────────────────────

function CategoriesTab({
  categories,
  subcategories,
  loading,
  onRefresh,
}: {
  categories: AssetCategory[];
  subcategories: AssetSubcategory[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [catDialog, setCatDialog] = useState<CatDialog>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'category' | 'subcategory'; id: string; name: string } | null>(null);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const invalidateCats = () => {
    void queryClient.invalidateQueries({ queryKey: ['assets', 'categories'] });
    void queryClient.invalidateQueries({ queryKey: ['assets', 'subcategories'] });
  };

  const deleteCatMut = useMutation({
    mutationFn: (id: string) => assetsService.deleteCategory(id),
    onSuccess: () => { toast.success('Category deleted'); invalidateCats(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteSubcatMut = useMutation({
    mutationFn: (id: string) => assetsService.deleteSubcategory(id),
    onSuccess: () => { toast.success('Subcategory deleted'); invalidateCats(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const subcatsByCategory = (catId: string) =>
    subcategories.filter((s) => s.category_id === catId);

  return (
    <>
      <div className="flex justify-end gap-2 mb-4">
        <PermissionGate permission={PERMISSIONS.ASSETS_MASTER}>
          <Button
            variant="outline"
            onClick={() => setCatDialog({ type: 'subcategory', mode: 'create' })}
          >
            + Sub Category
          </Button>
          <Button onClick={() => setCatDialog({ type: 'category', mode: 'create' })}>
            + Category
          </Button>
        </PermissionGate>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : categories.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No categories yet. Create your first category to get started.</p>
      ) : (
        <div className="space-y-2">
          {categories.map((cat) => {
            const subs = subcatsByCategory(cat.id);
            const isOpen = expanded.has(cat.id);
            return (
              <div key={cat.id} className="bg-card border rounded-xl p-4">
                {/* Category header */}
                <div className="flex items-center gap-3">
                  <Layers className="h-4 w-4 text-primary shrink-0" />
                  <span className="font-medium">{cat.name}</span>
                  <code className="bg-muted text-xs px-1.5 py-0.5 rounded font-mono">{cat.code}</code>
                  <div className="ml-auto flex items-center gap-1">
                    <PermissionGate permission={PERMISSIONS.ASSETS_MASTER}>
                      <button
                        type="button"
                        onClick={() => setCatDialog({ type: 'category', mode: 'edit', category: cat })}
                        className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        aria-label="Edit category"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget({ type: 'category', id: cat.id, name: cat.name })}
                        className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        aria-label="Delete category"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </PermissionGate>
                    <button
                      type="button"
                      onClick={() => toggleExpand(cat.id)}
                      className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label={isOpen ? 'Collapse' : 'Expand'}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Subcategories */}
                {isOpen && (
                  <div className="mt-3 pl-6">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      Sub Categories ({subs.length})
                    </p>
                    {subs.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No subcategories yet.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {subs.map((sub) => (
                          <div
                            key={sub.id}
                            className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-sm"
                          >
                            <span className="font-medium truncate min-w-0">{sub.name}</span>
                            <code className="bg-muted text-xs px-1.5 py-0.5 rounded font-mono shrink-0">{sub.code}</code>
                            {sub.description && (
                              <span className="text-xs text-muted-foreground truncate min-w-0 flex-1">
                                {sub.description}
                              </span>
                            )}
                            <PermissionGate permission={PERMISSIONS.ASSETS_MASTER}>
                              <div className="ml-auto flex items-center gap-0.5 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setCatDialog({ type: 'subcategory', mode: 'edit', subcategory: sub, preselectedCategoryId: cat.id })}
                                  className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                  aria-label="Edit subcategory"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleteTarget({ type: 'subcategory', id: sub.id, name: sub.name })}
                                  className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                  aria-label="Delete subcategory"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </PermissionGate>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {catDialog?.type === 'category' && (
        <CategoryFormDialog
          open
          mode={catDialog.mode}
          category={catDialog.category}
          onClose={() => setCatDialog(null)}
          onSuccess={() => { setCatDialog(null); onRefresh(); }}
        />
      )}
      {catDialog?.type === 'subcategory' && (
        <SubcategoryFormDialog
          open
          mode={catDialog.mode}
          subcategory={catDialog.subcategory}
          preselectedCategoryId={catDialog.preselectedCategoryId}
          categories={categories}
          onClose={() => setCatDialog(null)}
          onSuccess={() => { setCatDialog(null); onRefresh(); }}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.type === 'category' ? 'Category' : 'Subcategory'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'category'
                ? `Delete "${deleteTarget.name}"? All subcategories will also be removed. This cannot be undone.`
                : `Delete "${deleteTarget?.name}"? This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteTarget) return;
                if (deleteTarget.type === 'category') deleteCatMut.mutate(deleteTarget.id);
                else deleteSubcatMut.mutate(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Brands Tab ───────────────────────────────────────────────────────────────

function BrandsTab({ brands, loading, onRefresh }: { brands: AssetBrand[]; loading: boolean; onRefresh: () => void }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<BrandDialog>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => assetsService.deleteBrand(id),
    onSuccess: () => {
      toast.success('Brand deleted');
      void queryClient.invalidateQueries({ queryKey: ['assets', 'brands'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<AssetBrand>[] = [
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'code', header: 'Code', cell: (r) => <code className="font-mono text-xs">{r.code}</code> },
    {
      key: 'description',
      header: 'Description',
      cell: (r) => r.description
        ? <span className="text-sm text-muted-foreground truncate max-w-xs block">{r.description}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <PermissionGate permission={PERMISSIONS.ASSETS_MASTER}>
            <button
              type="button"
              onClick={() => setDialog({ mode: 'edit', brand: r })}
              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setDeleteTarget({ id: r.id, name: r.name });
              }}
              className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              aria-label="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <PermissionGate permission={PERMISSIONS.ASSETS_MASTER}>
          <Button onClick={() => setDialog({ mode: 'create' })}>+ Add</Button>
        </PermissionGate>
      </div>
      <DataTable
        columns={columns}
        rows={brands}
        rowKey={(r) => r.id}
        loading={loading}
        empty="No brands yet."
      />
      {dialog && (
        <BrandFormDialog
          open
          mode={dialog.mode}
          brand={dialog.brand}
          onClose={() => setDialog(null)}
          onSuccess={() => { setDialog(null); onRefresh(); }}
        />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Brand</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Status Tab ───────────────────────────────────────────────────────────────

function StatusTab({ statuses, loading, onRefresh }: { statuses: AssetStatus[]; loading: boolean; onRefresh: () => void }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<StatusDialog>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => assetsService.deleteStatus(id),
    onSuccess: () => {
      toast.success('Status deleted');
      void queryClient.invalidateQueries({ queryKey: ['assets', 'statuses'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<AssetStatus>[] = [
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'code', header: 'Code', cell: (r) => <code className="font-mono text-xs">{r.code}</code> },
    {
      key: 'color',
      header: 'Color',
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span className="h-4 w-4 rounded-full shrink-0 border border-border/40" style={{ backgroundColor: r.color }} />
          <code className="text-xs">{r.color}</code>
        </div>
      ),
    },
    { key: 'order', header: 'Order', cell: (r) => r.display_order },
    {
      key: 'default',
      header: 'Default',
      cell: (r) => r.is_default ? <Badge variant="success">Default</Badge> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right',
      cell: (r) => {
        const canDelete = !r.is_default;
        return (
          <div className="flex items-center justify-end gap-0.5">
            <PermissionGate permission={PERMISSIONS.ASSETS_MASTER}>
              <button
                type="button"
                onClick={() => setDialog({ mode: 'edit', status: r })}
                className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={!canDelete}
                onClick={() => {
                  if (!canDelete) return;
                  setDeleteTarget({ id: r.id, name: r.name });
                }}
                className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-40"
                aria-label="Delete"
                title={r.is_default ? 'Cannot delete the default status' : undefined}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </PermissionGate>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <PermissionGate permission={PERMISSIONS.ASSETS_MASTER}>
          <Button onClick={() => setDialog({ mode: 'create' })}>+ Add</Button>
        </PermissionGate>
      </div>
      <DataTable
        columns={columns}
        rows={statuses}
        rowKey={(r) => r.id}
        loading={loading}
        empty="No statuses yet."
      />
      {dialog && (
        <StatusFormDialog
          open
          mode={dialog.mode}
          status={dialog.status}
          onClose={() => setDialog(null)}
          onSuccess={() => { setDialog(null); onRefresh(); }}
        />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Status</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────

function SettingsTab() {
  const queryClient = useQueryClient();

  const prefixQuery = useQuery({
    queryKey: ['assets', 'config', 'prefix'],
    queryFn: () => assetsService.getCodePrefix(),
  });

  // getCodePrefix() returns a plain string
  const queriedPrefix = prefixQuery.data ?? '';
  const [localPrefix, setLocalPrefix] = useState<string | null>(null);
  const effectivePrefix = localPrefix ?? queriedPrefix;

  const saveMut = useMutation({
    mutationFn: (value: string) => assetsService.setCodePrefix(value),
    onSuccess: () => {
      toast.success('Prefix saved');
      void queryClient.invalidateQueries({ queryKey: ['assets', 'config', 'prefix'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Asset Code Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="asset-prefix">Code Prefix</Label>
          <Input
            id="asset-prefix"
            value={effectivePrefix}
            onChange={(e) => setLocalPrefix(e.target.value.toUpperCase().slice(0, 20))}
            placeholder="e.g. RRC"
            maxLength={20}
            disabled={prefixQuery.isLoading}
          />
          <p className="text-xs text-muted-foreground">
            Prefix used in asset codes, e.g. &apos;RRC&apos; generates codes like RRC-CD-LP-23
          </p>
        </div>
        <Button
          onClick={() => saveMut.mutate(effectivePrefix)}
          disabled={saveMut.isPending || prefixQuery.isLoading}
        >
          {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AssetsMasterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'categories';

  const setTab = (key: string) => setSearchParams({ tab: key });

  const { has } = usePermission();
  const canManageIdCategories = has(PERMISSIONS.ID_CATEGORIES_MANAGE);
  const tabs = canManageIdCategories ? [...TABS, { key: 'id-categories', label: 'ID Categories' }] : TABS;

  const categoriesQuery = useQuery({
    queryKey: ['assets', 'categories'],
    queryFn: () => assetsService.listCategories(),
  });

  const subcategoriesQuery = useQuery({
    queryKey: ['assets', 'subcategories'],
    queryFn: () => assetsService.listSubcategories(),
  });

  const brandsQuery = useQuery({
    queryKey: ['assets', 'brands'],
    queryFn: () => assetsService.listBrands(),
  });

  const statusesQuery = useQuery({
    queryKey: ['assets', 'statuses'],
    queryFn: () => assetsService.listStatuses(),
  });

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold text-foreground">Assets Master</h1>

      <Tabs tabs={tabs} active={activeTab} onChange={setTab} />

      <div className="mt-4">
        {activeTab === 'categories' && (
          <CategoriesTab
            categories={categoriesQuery.data ?? []}
            subcategories={subcategoriesQuery.data ?? []}
            loading={categoriesQuery.isLoading || subcategoriesQuery.isLoading}
            onRefresh={() => {
              void categoriesQuery.refetch();
              void subcategoriesQuery.refetch();
            }}
          />
        )}
        {activeTab === 'brands' && (
          <BrandsTab
            brands={brandsQuery.data ?? []}
            loading={brandsQuery.isLoading}
            onRefresh={() => void brandsQuery.refetch()}
          />
        )}
        {activeTab === 'status' && (
          <StatusTab
            statuses={statusesQuery.data ?? []}
            loading={statusesQuery.isLoading}
            onRefresh={() => void statusesQuery.refetch()}
          />
        )}
        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'id-categories' && canManageIdCategories && <IdCategoriesTab />}
      </div>
    </div>
  );
}
